import React, { useState, useRef, useEffect } from 'react';
import '../css/LanguageTranslator.css';
import { languages } from '../data/Languages';

export default function TranslatorPage() {
  const [fromLanguage, setFromLanguage] = useState('en');
  const [toLanguages, setToLanguages] = useState(['da', 'sv', 'de', 'es']);
  const [inputText, setInputText] = useState('');
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [hasConverted, setHasConverted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  
  // Search states
  const [fromSearchTerm, setFromSearchTerm] = useState('');
  const [toSearchTerm, setToSearchTerm] = useState('');
  const [isFromDropdownOpen, setIsFromDropdownOpen] = useState(false);
  const [isToDropdownOpen, setIsToDropdownOpen] = useState(false);
  
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  // Hero section features data
  const heroFeatures = [
    { icon: '🌍', title: '130+ Languages', description: 'Support for major world languages including English, Spanish, Chinese, Arabic, and more' },
    { icon: '⚡', title: 'Instant Translation', description: 'Powered by Google Translate API for fast and accurate results in seconds' },
    { icon: '🔄', title: 'Multi-Language', description: 'Translate to multiple target languages simultaneously with one click' },
    { icon: '🔒', title: 'Privacy First', description: 'Your text is processed securely with no permanent storage' },
  ];

  // Sort languages by name for better UX
  const sortedLanguages = [...languages].sort((a, b) => a.name.localeCompare(b.name));

  // Get language info
  const getLanguageInfo = (code) => {
    return languages.find(lang => lang.code === code) || { code, name: code, flag: '🏳️', nativeName: code, region: 'Unknown', speakers: 'N/A' };
  };

  // Filter languages based on search term
  const getFilteredLanguages = (searchTerm) => {
    if (!searchTerm) return sortedLanguages;
    const term = searchTerm.toLowerCase();
    return sortedLanguages.filter(lang => 
      lang.name.toLowerCase().includes(term) ||
      lang.nativeName.toLowerCase().includes(term) ||
      lang.code.toLowerCase().includes(term) ||
      lang.region.toLowerCase().includes(term)
    );
  };

  // Get available languages for "To" dropdown (excluding already selected)
  const getAvailableLanguages = () => {
    return sortedLanguages.filter(lang => lang.code !== fromLanguage && !toLanguages.includes(lang.code));
  };

  // Add language to "To" languages
  const addLanguage = (langCode) => {
    if (!toLanguages.includes(langCode)) {
      setToLanguages([...toLanguages, langCode]);
    }
    setIsToDropdownOpen(false);
    setToSearchTerm('');
  };

  // Remove language from "To" languages
  const removeLanguage = (langCode) => {
    setToLanguages(toLanguages.filter(code => code !== langCode));
  };

  // Handle from language selection
  const handleFromLanguageSelect = (langCode) => {
    setFromLanguage(langCode);
    setIsFromDropdownOpen(false);
    setFromSearchTerm('');
  };

  // Translate text using Google Translate API
  const translateWithGoogle = async (text, targetLang) => {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLanguage}&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;
    
    try {
      const response = await fetch(url);
      const data = await response.json();
      
      if (data && data[0] && data[0][0] && data[0][0][0]) {
        const translatedText = data[0].map(segment => segment[0]).join('');
        return translatedText;
      }
      return 'Translation failed';
    } catch (error) {
      console.error('Google Translate error:', error);
      return 'Translation failed';
    }
  };

  // Translate text
  const handleConvert = async () => {
    if (!inputText.trim()) {
      alert('Please enter text to translate');
      return;
    }

    if (toLanguages.length === 0) {
      alert('Please select at least one language to translate to');
      return;
    }

    setLoading(true);
    const newTranslations = {};

    try {
      const translationPromises = toLanguages.map(async (langCode) => {
        try {
          const translatedText = await translateWithGoogle(inputText, langCode);
          return { langCode, translatedText };
        } catch (error) {
          console.error(`Error translating to ${langCode}:`, error);
          return { langCode, translatedText: 'Translation failed' };
        }
      });

      const results = await Promise.all(translationPromises);
      
      results.forEach(result => {
        newTranslations[result.langCode] = result.translatedText;
      });
      
      setTranslations(newTranslations);
      setHasConverted(true);
    } catch (error) {
      console.error('Translation error:', error);
      alert('Translation failed. Please try again.');
    }

    setLoading(false);
  };

  // Handle text input change
  const handleTextChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    setCharCount(text.length);
  };

  // Clear everything
  const handleClear = () => {
    setInputText('');
    setTranslations({});
    setHasConverted(false);
    setCopiedId(null);
    setCharCount(0);
  };

  // Copy to clipboard
  const handleCopy = (text, langCode) => {
    navigator.clipboard.writeText(text);
    setCopiedId(langCode);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target)) {
        setIsFromDropdownOpen(false);
      }
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target)) {
        setIsToDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-rotate hero feature highlights
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % heroFeatures.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const fromLang = getLanguageInfo(fromLanguage);
  const filteredFromLanguages = getFilteredLanguages(fromSearchTerm);
  const filteredToLanguages = getFilteredLanguages(toSearchTerm);
  const availableToAdd = getAvailableLanguages();

  // Split translations into rows of 2
  const getTranslationRows = () => {
    const rows = [];
    for (let i = 0; i < toLanguages.length; i += 2) {
      rows.push({
        left: toLanguages[i],
        right: toLanguages[i + 1] || null
      });
    }
    return rows;
  };

  const translationRows = getTranslationRows();

  return (
    <div className="trans-container">
      <h1 className="sr-only">Language Translator</h1>

      <div className="trans-wrapper">
        {/* Enhanced Hero Section */}
        <div className="trans-hero">
          <div className="trans-hero-badge">
            <span className="trans-hero-badge-icon">🚀</span>
            <span>Powered by Google Translate API • 130+ Languages Supported</span>
          </div>
          
          <h1 className="trans-hero-title">
            Break Language Barriers
            <span className="trans-hero-title-gradient"> Instantly</span>
          </h1>
          
          <p className="trans-hero-description">
            Translate text between 130+ languages with our fast, accurate, and easy-to-use translator. 
            Perfect for students, travelers, professionals, and global communicators.
          </p>
          
          {/* <div className="trans-hero-stats">
            <div className="trans-hero-stat">
              <span className="trans-hero-stat-number">130+</span>
              <span className="trans-hero-stat-label">Languages</span>
            </div>
            <div className="trans-hero-stat-divider"></div>
            <div className="trans-hero-stat">
              <span className="trans-hero-stat-number">5M+</span>
              <span className="trans-hero-stat-label">Daily Translations</span>
            </div>
            <div className="trans-hero-stat-divider"></div>
            <div className="trans-hero-stat">
              <span className="trans-hero-stat-number">98%</span>
              <span className="trans-hero-stat-label">Accuracy Rate</span>
            </div>
          </div> */}
          
          <div className="trans-hero-features">
            {heroFeatures.map((feature, index) => (
              <div 
                key={index} 
                className={`trans-hero-feature ${activeFeature === index ? 'active' : ''}`}
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="trans-hero-feature-icon">{feature.icon}</div>
                <div className="trans-hero-feature-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Card */}
        <div className="trans-main-card">
          {/* Selector Section */}
          <div className="trans-selector-section">
            <div className="trans-selector-grid">
              {/* From Language - Searchable Dropdown */}
              <div className="trans-selector-group">
                <label className="trans-selector-label">
                  <span className="trans-selector-label-icon">📤</span>
                  Translate From
                </label>
                <div className="trans-searchable-dropdown" ref={fromDropdownRef}>
                  <div 
                    className="trans-dropdown-trigger"
                    onClick={() => setIsFromDropdownOpen(!isFromDropdownOpen)}
                  >
                    <div className="trans-selected-language">
                      <span className="trans-flag">{fromLang.flag}</span>
                      <span className="trans-language-name">{fromLang.name}</span>
                      <span className="trans-language-code">{fromLang.code.toUpperCase()}</span>
                    </div>
                    <span className="trans-dropdown-arrow">{isFromDropdownOpen ? '▲' : '▼'}</span>
                  </div>
                  
                  {isFromDropdownOpen && (
                    <div className="trans-dropdown-menu">
                      <div className="trans-dropdown-search">
                        <input
                          type="text"
                          placeholder="Search languages..."
                          value={fromSearchTerm}
                          onChange={(e) => setFromSearchTerm(e.target.value)}
                          className="trans-search-input"
                          autoFocus
                        />
                      </div>
                      <div className="trans-dropdown-list">
                        {filteredFromLanguages.map((lang) => (
                          <div
                            key={lang.code}
                            className={`trans-dropdown-item ${fromLanguage === lang.code ? 'selected' : ''}`}
                            onClick={() => handleFromLanguageSelect(lang.code)}
                          >
                            <span className="trans-flag">{lang.flag}</span>
                            <div className="trans-dropdown-item-info">
                              <span className="trans-dropdown-item-name">{lang.name}</span>
                              <span className="trans-dropdown-item-native">{lang.nativeName}</span>
                            </div>
                            <span className="trans-dropdown-item-region">{lang.region}</span>
                          </div>
                        ))}
                        {filteredFromLanguages.length === 0 && (
                          <div className="trans-dropdown-no-results">No languages found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <p className="trans-selector-hint">
                  Source language - your original text
                </p>
              </div>

              {/* To Language - Searchable Dropdown */}
              <div className="trans-selector-group">
                <label className="trans-selector-label">
                  <span className="trans-selector-label-icon">📥</span>
                  Translate To
                </label>
                <div className="trans-searchable-dropdown" ref={toDropdownRef}>
                  <div 
                    className="trans-dropdown-trigger"
                    onClick={() => setIsToDropdownOpen(!isToDropdownOpen)}
                  >
                    <div className="trans-selected-language">
                      <span className="trans-add-icon">+</span>
                      <span className="trans-language-name">Add language...</span>
                    </div>
                    <span className="trans-dropdown-arrow">{isToDropdownOpen ? '▲' : '▼'}</span>
                  </div>
                  
                  {isToDropdownOpen && (
                    <div className="trans-dropdown-menu">
                      <div className="trans-dropdown-search">
                        <input
                          type="text"
                          placeholder="Search languages..."
                          value={toSearchTerm}
                          onChange={(e) => setToSearchTerm(e.target.value)}
                          className="trans-search-input"
                          autoFocus
                        />
                      </div>
                      <div className="trans-dropdown-list">
                        {filteredToLanguages
                          .filter(lang => lang.code !== fromLanguage && !toLanguages.includes(lang.code))
                          .map((lang) => (
                            <div
                              key={lang.code}
                              className="trans-dropdown-item"
                              onClick={() => addLanguage(lang.code)}
                            >
                              <span className="trans-flag">{lang.flag}</span>
                              <div className="trans-dropdown-item-info">
                                <span className="trans-dropdown-item-name">{lang.name}</span>
                                <span className="trans-dropdown-item-native">{lang.nativeName}</span>
                              </div>
                              <span className="trans-dropdown-item-region">{lang.region}</span>
                            </div>
                          ))}
                        {filteredToLanguages.filter(lang => lang.code !== fromLanguage && !toLanguages.includes(lang.code)).length === 0 && (
                          <div className="trans-dropdown-no-results">No languages available</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <p className="trans-selector-hint">
                  Add languages to translate into
                </p>
              </div>
            </div>

            {/* Selected Languages Tags */}
            {toLanguages.length > 0 && (
              <div className="trans-to-languages">
                <label className="trans-to-languages-label">
                  🎯 Target Languages ({toLanguages.length})
                </label>
                <div className="trans-languages-tags">
                  {toLanguages.map((langCode) => {
                    const lang = getLanguageInfo(langCode);
                    return (
                      <div key={langCode} className="trans-language-tag">
                        <span>{lang.flag} {lang.name}</span>
                        <button
                          className="trans-language-tag-remove"
                          onClick={() => removeLanguage(langCode)}
                          aria-label={`Remove ${lang.name}`}
                          title={`Remove ${lang.name}`}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Input Text Area */}
            <div className="trans-input-group">
              <label className="trans-selector-label" htmlFor="input-text">
                <span className="trans-selector-label-icon">📝</span>
                Enter Your Text
              </label>
              <textarea
                id="input-text"
                value={inputText}
                onChange={handleTextChange}
                placeholder="Type or paste your text here... (max 5000 characters)"
                maxLength={5000}
                className="trans-input"
                rows={5}
              />
              <div className="trans-input-footer">
                <span className={`trans-char-count ${charCount > 4500 ? 'warning' : ''}`}>
                  {charCount} / 5000 characters
                </span>
                {inputText && (
                  <span className="trans-language-detected">
                    Detected: {fromLang?.flag} {fromLang?.name}
                  </span>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="trans-buttons-group">
              <button
                onClick={handleConvert}
                disabled={loading || !inputText.trim()}
                className="trans-btn trans-btn-primary"
              >
                {loading ? (
                  <>
                    <span className="trans-loader"></span>
                    Translating...
                  </>
                ) : (
                  <>🌐 Translate Now</>
                )}
              </button>
              <button
                onClick={handleClear}
                disabled={!inputText && !hasConverted}
                className="trans-btn trans-btn-secondary"
              >
                🗑️ Clear All
              </button>
            </div>
          </div>

          {/* Results Section */}
          {hasConverted && (
            <div className="trans-results-wrapper">
              <div className="trans-results-header">
                <h2>📋 Translation Results</h2>
                <p>Your text has been translated to {toLanguages.length} language{toLanguages.length > 1 ? 's' : ''}</p>
              </div>
              <div className="trans-results-section">
                {/* Row 1: Original + First Translation */}
                <div className="trans-result-row">
                  <div className="trans-result-col">
                    <h3 className="trans-result-column-title">
                      {fromLang?.flag} {fromLang?.name} (Original)
                    </h3>
                    <div className="trans-input-preview">
                      {inputText || '(empty)'}
                    </div>
                  </div>

                  {toLanguages[0] && (
                    <div className="trans-result-col">
                      {(() => {
                        const lang = getLanguageInfo(toLanguages[0]);
                        const translatedText = translations[toLanguages[0]] || '';
                        return (
                          <>
                            <h3 className="trans-result-column-title">
                              {lang?.flag} {lang?.name}
                            </h3>
                            <div className="trans-translation-wrapper">
                              <p className="trans-translation-text">
                                {translatedText || 'Translating...'}
                              </p>
                              <button
                                className={`trans-copy-btn ${copiedId === toLanguages[0] ? 'copied' : ''}`}
                                onClick={() => handleCopy(translatedText, toLanguages[0])}
                                disabled={!translatedText || translatedText === 'Translating...'}
                                title="Copy translation"
                              >
                                {copiedId === toLanguages[0] ? '✓ Copied!' : '📋 Copy'}
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Remaining translations in pairs */}
                {toLanguages.slice(1).map((langCode, index) => {
                  if (index % 2 === 0) {
                    const nextLang = toLanguages[index + 2];
                    const currentLangInfo = getLanguageInfo(langCode);
                    const nextLangInfo = nextLang ? getLanguageInfo(nextLang) : null;
                    const currentText = translations[langCode] || '';
                    const nextText = nextLang ? translations[nextLang] || '' : null;
                    
                    return (
                      <div key={langCode} className="trans-result-row">
                        <div className="trans-result-col">
                          <h3 className="trans-result-column-title">
                            {currentLangInfo?.flag} {currentLangInfo?.name}
                          </h3>
                          <div className="trans-translation-wrapper">
                            <p className="trans-translation-text">
                              {currentText || 'Translating...'}
                            </p>
                            <button
                              className={`trans-copy-btn ${copiedId === langCode ? 'copied' : ''}`}
                              onClick={() => handleCopy(currentText, langCode)}
                              disabled={!currentText || currentText === 'Translating...'}
                              title="Copy translation"
                            >
                              {copiedId === langCode ? '✓ Copied!' : '📋 Copy'}
                            </button>
                          </div>
                        </div>
                        
                        {nextLang && (
                          <div className="trans-result-col">
                            <h3 className="trans-result-column-title">
                              {nextLangInfo?.flag} {nextLangInfo?.name}
                            </h3>
                            <div className="trans-translation-wrapper">
                              <p className="trans-translation-text">
                                {nextText || 'Translating...'}
                              </p>
                              <button
                                className={`trans-copy-btn ${copiedId === nextLang ? 'copied' : ''}`}
                                onClick={() => handleCopy(nextText, nextLang)}
                                disabled={!nextText || nextText === 'Translating...'}
                                title="Copy translation"
                              >
                                {copiedId === nextLang ? '✓ Copied!' : '📋 Copy'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!hasConverted && !inputText && (
            <div className="trans-empty-state">
              <div className="trans-empty-icon">🌍</div>
              <h3>Ready to Translate</h3>
              <p className="trans-empty-text">
                Select your source and target languages above, then enter text to get started
              </p>
              <div className="trans-empty-tips">
                <span>💡 Tip: You can translate to multiple languages at once!</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="trans-footer">
          <p>Powered by Google Translate API • Supporting 130+ languages worldwide • Free for personal use</p>
        </div>
      </div>
    </div>
  );
}